import styled from '@emotion/styled'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { colors } from '@/styles/theme'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '@/components/atoms/Inputs'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Sex } from '@/firebase/firestore/Sex'
import { MonsterHunterRise1 } from './MonsterHunterRise1'
import { MonsterHunterRiseSelector } from './MonsterHunterRiseSelector'
import { Platform } from '@/firebase/firestore/Platform'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { useLocale } from '@/hooks/useLocale'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'

/**
 * プラットフォーム事にフォームが違う
 *
 */

type FormField = {
  id: string
  name: string
  playStyle: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  hunterRank: number
  hunterHistory: string
  hitokoto: string
}

type Props = {
  onCreate: (
    data: FormField & { selectedWeapons: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  creatorParam: string | null
}

const FONT_COLORS = [
  '#FD8080',
  '#FD7C24',
  '#29DDF2',
  '#C05AE0',
  '#7AA852',
  '#4C4C4C',
]

const TARTET_ID = 'monsterhunterrise1'

export const EditorMonsterHunterRise: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
  creatorParam,
}) => {
  const { t } = useLocale()
  const { handleSubmit, register, watch } = useForm<FormField>({
    defaultValues: {
      playTimeWeekDayBegin: '18',
      playTimeWeekDayEnd: '19',
      playTimeWeekEndBegin: '18',
      playTimeWeekEndEnd: '19',
    },
  })
  const fieldVal = watch()
  const [selectedColor, setColor] = useState(0)
  const onChangeColor = useCallback((selectedIndex: number) => {
    setColor(selectedIndex)
  }, [])
  const [selectedFontColor, setFontColor] = useState(0)
  const onChangeFontColor = useCallback((selectedIndex: number) => {
    setFontColor(selectedIndex)
  }, [])
  const [selectedSex, setSex] = useState<Sex>(0)
  const onChangeSex = useCallback((selectedIndex: Sex) => {
    setSex(selectedIndex)
  }, [])

  const [selectedPlatform, setPlatform] = useState<Array<Platform>>([0])
  const onChangePlatform = useCallback((selectedIndex: Array<Platform>) => {
    setPlatform(selectedIndex)
  }, [])

  const [selectedVoiceChat, setVoiceChat] = useState<Array<VoiceChat>>([])
  const onChangeVoiceChat = useCallback((selectedIndex: Array<VoiceChat>) => {
    setVoiceChat(selectedIndex)
  }, [])

  const [selectedWeapons, setWeapons] = useState<number[]>([])
  const onChangeWeapon = useCallback((selectedIndex: number[]) => {
    setWeapons(selectedIndex)
  }, [])

  const handleCreate = useCallback((data: FormField) => {
    onCreate(
      { ...data, selectedWeapons },
      TARTET_ID,
      selectedSex,
      selectedPlatform,
      selectedVoiceChat
    )
  }, [])

  return (
    <>
      <PreviewContainer>
        <PreviewCardContainer>
          <MonsterHunterRise1
            id={TARTET_ID}
            uid={fieldVal.id}
            fontColor={FONT_COLORS[selectedFontColor]}
            name={fieldVal.name}
            sex={selectedSex}
            templateColor={selectedColor as 0 | 1 | 2 | 3}
            platform={selectedPlatform}
            playStyle={fieldVal.playStyle}
            voiceChat={selectedVoiceChat}
            hunterRank={fieldVal.hunterRank}
            hunterHistory={fieldVal.hunterHistory}
            playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
            playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
            playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
            playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
            weapons={selectedWeapons}
            hitokoto={fieldVal.hitokoto}
          />
        </PreviewCardContainer>
      </PreviewContainer>
      <DescriptionSection>
        <DescriptionLabel>
          モンスターハンターライズ(モンハンライズ)の自己紹介カードが簡単に作成・編集できるツールです！
        </DescriptionLabel>
        <DescriptionLabel>
          プレビューを見ながらサイトに直接入力してあなただけの自己紹介カードが作れちゃいます🥳🎉
        </DescriptionLabel>
        <ProfileCardEditorDescription />
      </DescriptionSection>
      <FormContainer>
        <form
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          onSubmit={handleSubmit(handleCreate)}
        >
          <FormItemContainer>
            <Label>カードの背景色を選ぶ</Label>
            <ColorSelector
              colors={['#F4B5B4', '#A3D6C5', '#FFE3BE', '#A9BBFF']}
              value={selectedColor}
              onChange={onChangeColor}
            />
          </FormItemContainer>
          <FormItemContainer>
            <Label>文字色を選ぶ</Label>
            <ColorSelector
              colors={FONT_COLORS}
              value={selectedFontColor}
              onChange={onChangeFontColor}
            />
          </FormItemContainer>
          <FormItemContainer>
            <Label>{t.なまえ}</Label>
            <InputText {...register('name', { maxLength: 20 })} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>{t.ID}</Label>
            <InputText {...register('id', { maxLength: 20 })} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>性別</Label>
            <SexSelector val={selectedSex} onChange={onChangeSex} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>HR</Label>
            <InputText type="number" {...register('hunterRank')} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>ハンター歴</Label>
            <InputText {...register('hunterHistory', { maxLength: 50 })} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>プラットフォーム</Label>
            <PlatformSelector
              activeValues={[0, 3]}
              onChange={onChangePlatform}
              value={selectedPlatform}
            />
          </FormItemContainer>
          <FormItemContainer>
            <Label>プレイスタイル</Label>
            <InputText {...register('playStyle', { maxLength: 20 })} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>ボイスチャット</Label>
            <VoiceChatSelector
              onChange={onChangeVoiceChat}
              value={selectedVoiceChat}
              activeValue={[3, 0, 1, 4]}
            />
          </FormItemContainer>
          <FormItemContainer>
            <Label>{t.プレイ時間帯平日}</Label>
            <InputSelector {...register('playTimeWeekDayBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekDayEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>
          <FormItemContainer>
            <Label>{t.プレイ時間帯土日}</Label>
            <InputSelector {...register('playTimeWeekEndBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekEndEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>
          <FormItemContainer>
            <Label>よく使う武器</Label>
            <MonsterHunterRiseSelector
              onChange={onChangeWeapon}
              currentValue={selectedWeapons}
            />
          </FormItemContainer>
          <FormItemContainer>
            <Label>ひとこと</Label>
            <InputText {...register('hitokoto', { maxLength: 50 })} />
          </FormItemContainer>
          <Submit
            id="create_profile"
            type="submit"
            value="これでつくる！"
            disabled={submitButtonDisable}
          />
        </form>
      </FormContainer>
    </>
  )
}

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 32px 16px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`
const DescriptionSection = styled.label`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
`

const DescriptionLabel = styled.label`
  font-weight: bold;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: center;
  margin-bottom: 12px;
  display: block;
`

const Label = styled.label`
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  margin-bottom: 16px;
  display: block;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const Submit = styled.input`
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`
